import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, LogIn, Mail, Lock, Shield, KeyRound, ArrowLeft, CheckCircle, Eye, EyeOff, Clock, AlertTriangle, RefreshCw, Smartphone } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const { login, setUser, setToken, getCurrentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Steps: 'credentials', '2fa', 'forgot', 'forgot-choose', 'reset', 'reset-totp', 'totp-setup'
  const [step, setStep] = useState('credentials');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [twoFAData, setTwoFAData] = useState({ tempToken: '', code: '', method: 'email', totpEnabled: false });
  const [resetData, setResetData] = useState({ email: '', code: '', newPassword: '', confirmPassword: '', method: 'email' });
  const [totpSetup, setTotpSetup] = useState({ qrCode: '', secret: '', verifyCode: '' });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [codeExpiresAt, setCodeExpiresAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const timerRef = useRef(null);
  const cooldownRef = useRef(null);

  useEffect(() => {
    if (codeExpiresAt && (step === 'reset' || step === 'reset-totp')) {
      const tick = () => {
        const remaining = Math.max(0, Math.floor((codeExpiresAt - Date.now()) / 1000));
        setTimeLeft(remaining);
        if (remaining <= 0) { clearInterval(timerRef.current); timerRef.current = null; }
      };
      tick();
      timerRef.current = setInterval(tick, 1000);
      return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [codeExpiresAt, step]);

  useEffect(() => {
    if (resendCooldown > 0) {
      cooldownRef.current = setInterval(() => {
        setResendCooldown(prev => { if (prev <= 1) { clearInterval(cooldownRef.current); return 0; } return prev - 1; });
      }, 1000);
      return () => clearInterval(cooldownRef.current);
    }
  }, [resendCooldown]);

  const handleResendCode = async () => {
    setResending(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/auth/password/forgot`, { email: resetData.email });
      setCodeExpiresAt(Date.now() + 15 * 60 * 1000);
      setResendCooldown(60);
      setResetData(prev => ({ ...prev, code: '' }));
      toast({ title: "Cod nou trimis", description: "Un cod nou a fost trimis pe email." });
    } catch {
      toast({ title: "Cod nou trimis", description: "Daca email-ul exista, veti primi un cod nou." });
      setResendCooldown(60);
    } finally { setResending(false); }
  };

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); if (error) setError(''); };
  const handleTwoFAChange = (e) => { setTwoFAData({ ...twoFAData, code: e.target.value.replace(/\D/g, '').slice(0, 6) }); if (error) setError(''); };
  const handleResetChange = (e) => { setResetData({ ...resetData, [e.target.name]: e.target.value }); if (error) setError(''); };

  // Step 1: Verifică email + parolă, apoi cere ÎNTOTDEAUNA codul 2FA
  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/api/auth/login/step1`, {
        email: formData.email, password: formData.password
      });
      // Bypass 2FA (doar preview): backend întoarce direct tokenul final
      if (response.data.requires_2fa === false && response.data.access_token) {
        const token = response.data.access_token;
        localStorage.setItem('token', token);
        if (response.data.refresh_token) localStorage.setItem('refreshToken', response.data.refresh_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        if (setToken) setToken(token);
        await finalizeLogin();
        return;
      }
      const { temp_token, totp_enabled } = response.data;
      setTwoFAData({
        tempToken: temp_token,
        code: '',
        method: totp_enabled ? 'totp' : 'email',
        totpEnabled: !!totp_enabled
      });
      setStep('2fa');
    } catch (error) {
      setError(error.response?.data?.detail || 'Email sau parolă incorectă');
    } finally { setLoading(false); }
  };

  const finalizeLogin = async () => {
    await getCurrentUser();
    handleSuccessfulLogin();
  };

  // Confirm TOTP setup
  const handleTotpSetupConfirm = async () => {
    if (totpSetup.verifyCode.length !== 6) { setError('Codul trebuie sa aiba 6 cifre'); return; }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/auth/totp/confirm`, { code: totpSetup.verifyCode }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Google Authenticator activat!", description: "De acum veți folosi codul din aplicație la fiecare autentificare." });
      await finalizeLogin();
    } catch (err) {
      setError(err.response?.data?.detail || 'Cod invalid. Verificati si incercati din nou.');
    } finally { setLoading(false); }
  };

  // Step 2: Verifică codul 2FA (email sau TOTP) și emite tokenul final
  const handleTwoFASubmit = async (e) => {
    e.preventDefault();
    if (twoFAData.code.length !== 6) { setError('Codul trebuie sa aiba 6 cifre'); return; }
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/api/auth/login/step2`, {
        temp_token: twoFAData.tempToken,
        code: twoFAData.code,
        method: twoFAData.method
      });
      const token = response.data.access_token;
      localStorage.setItem('token', token);
      if (response.data.refresh_token) localStorage.setItem('refreshToken', response.data.refresh_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (setToken) setToken(token);

      // Primul login (fără TOTP): înrolăm Google Authenticator pentru data viitoare
      if (!twoFAData.totpEnabled) {
        try {
          const setupRes = await axios.post(`${API_URL}/api/auth/totp/setup`, {}, { headers: { Authorization: `Bearer ${token}` } });
          setTotpSetup({ qrCode: setupRes.data.qr_code, secret: setupRes.data.secret, verifyCode: '' });
          setStep('totp-setup');
          setLoading(false);
          return;
        } catch (err) {
          console.log('TOTP enrollment skipped:', err);
        }
      }
      await finalizeLogin();
    } catch (error) {
      setError(error.response?.data?.detail || 'Cod invalid sau expirat');
    } finally { setLoading(false); }
  };

  // Forgot password - check TOTP status
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const totpCheck = await axios.get(`${API_URL}/api/auth/totp/check/${resetData.email}`);
      if (totpCheck.data.totp_enabled) {
        setStep('forgot-choose');
      } else {
        // Nu are TOTP, trimite direct pe email
        await sendResetEmail();
      }
    } catch {
      // Daca check-ul esueaza, trimite email oricum
      await sendResetEmail();
    } finally { setLoading(false); }
  };

  const sendResetEmail = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/password/forgot`, { email: resetData.email });
    } catch { /* ignore - security */ }
    setCodeExpiresAt(Date.now() + 15 * 60 * 1000);
    setResendCooldown(60);
    setResetData(prev => ({ ...prev, method: 'email' }));
    setStep('reset');
    toast({ title: "Cod trimis", description: "Verificati email-ul. Codul este valabil 15 minute." });
  };

  const handleChooseTOTP = () => {
    setResetData(prev => ({ ...prev, method: 'totp' }));
    setStep('reset-totp');
  };

  const handleChooseEmail = async () => {
    setLoading(true);
    await sendResetEmail();
    setLoading(false);
  };

  // Reset password (email code)
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (resetData.newPassword !== resetData.confirmPassword) { setError('Parolele nu coincid'); return; }
    if (resetData.newPassword.length < 8) { setError('Parola trebuie sa aiba minim 8 caractere'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/auth/password/reset`, {
        email: resetData.email, code: resetData.code, new_password: resetData.newPassword
      });
      toast({ title: "Parola schimbata!", description: "Te poti autentifica cu noua parola." });
      setStep('credentials');
      setFormData({ email: resetData.email, password: '' });
      setResetData({ email: '', code: '', newPassword: '', confirmPassword: '', method: 'email' });
      setCodeExpiresAt(null);
    } catch (error) {
      setError(error.response?.data?.detail || 'Eroare la resetarea parolei');
    } finally { setLoading(false); }
  };

  // Reset password (TOTP code)
  const handleResetTOTPSubmit = async (e) => {
    e.preventDefault();
    if (resetData.newPassword !== resetData.confirmPassword) { setError('Parolele nu coincid'); return; }
    if (resetData.newPassword.length < 8) { setError('Parola trebuie sa aiba minim 8 caractere'); return; }
    if (resetData.code.length !== 6) { setError('Codul trebuie sa aiba 6 cifre'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/auth/password/reset-totp`, {
        email: resetData.email, code: resetData.code, new_password: resetData.newPassword
      });
      toast({ title: "Parola schimbata!", description: "Te poti autentifica cu noua parola." });
      setStep('credentials');
      setFormData({ email: resetData.email, password: '' });
      setResetData({ email: '', code: '', newPassword: '', confirmPassword: '', method: 'email' });
    } catch (error) {
      setError(error.response?.data?.detail || 'Cod TOTP invalid sau eroare la resetare');
    } finally { setLoading(false); }
  };

  const handleSuccessfulLogin = () => {
    toast({ title: "Conectare reusita!", description: "Bun venit in dashboard." });
    setTimeout(() => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          navigate(payload.role === 'admin' || payload.role === 'subadmin' ? '/admin' : '/broker');
        } catch { navigate('/broker'); }
      } else { navigate('/broker'); }
    }, 100);
  };

  const formatTimeLeft = useCallback(() => {
    const mins = Math.floor(timeLeft / 60);
    const secs = timeLeft % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, [timeLeft]);

  // ===== RENDER FORMS =====

  const renderCredentialsForm = () => (
    <form onSubmit={handleCredentialsSubmit} className="space-y-4" data-testid="login-form">
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      <div className="space-y-2">
        <Label htmlFor="email">Adresa de Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange}
            className="pl-10" placeholder="exemplu@email.com" required data-testid="login-email" />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">Parola</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input id="password" name="password" type={showPassword ? "text" : "password"} value={formData.password}
            onChange={handleChange} className="pl-10 pr-10" placeholder="Introduceti parola" required data-testid="login-password" />
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-gray-400 hover:text-gray-600">
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800" disabled={loading} data-testid="login-submit">
        {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Se verifica...</> : <><LogIn className="mr-2 h-4 w-4" />Intra in cont</>}
      </Button>
      <div className="text-center">
        <button type="button" onClick={() => { setStep('forgot'); setResetData(prev => ({ ...prev, email: formData.email })); }}
          className="text-sm text-blue-600 hover:text-blue-800" data-testid="forgot-password-link">Am uitat parola</button>
      </div>
    </form>
  );

  const renderTwoFAForm = () => (
    <form onSubmit={handleTwoFASubmit} className="space-y-4" data-testid="2fa-form">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          {twoFAData.method === 'totp' ? <Smartphone className="h-8 w-8 text-blue-600" /> : <Shield className="h-8 w-8 text-blue-600" />}
        </div>
        <p className="text-gray-600 text-sm">{message}</p>
        {twoFAData.totpEnabled && (
          <div className="flex gap-2 justify-center mt-3">
            <button type="button" onClick={() => setTwoFAData(prev => ({...prev, method: 'totp', code: ''}))}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${twoFAData.method === 'totp' ? 'bg-blue-100 border-blue-400 text-blue-700' : 'border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
              <Smartphone className="h-3 w-3 inline mr-1" />Authenticator
            </button>
            <button type="button" onClick={() => setTwoFAData(prev => ({...prev, method: 'email', code: ''}))}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${twoFAData.method === 'email' ? 'bg-blue-100 border-blue-400 text-blue-700' : 'border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
              <Mail className="h-3 w-3 inline mr-1" />Email
            </button>
          </div>
        )}
      </div>
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      <div className="space-y-2">
        <Label htmlFor="code">{twoFAData.method === 'totp' ? 'Cod din Google Authenticator' : 'Cod de verificare (6 cifre)'}</Label>
        <div className="relative">
          <KeyRound className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input id="code" name="code" type="text" inputMode="numeric" value={twoFAData.code} onChange={handleTwoFAChange}
            className="pl-10 text-center text-2xl tracking-widest font-mono" placeholder="000000" maxLength={6} required data-testid="2fa-code" />
        </div>
      </div>
      <Button type="submit" className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
        disabled={loading || twoFAData.code.length !== 6} data-testid="2fa-submit">
        {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Se verifica...</> : <><CheckCircle className="mr-2 h-4 w-4" />Verifica</>}
      </Button>
      <div className="text-center">
        <button type="button" onClick={() => setStep('credentials')} className="text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1 mx-auto">
          <ArrowLeft className="h-4 w-4" />Inapoi
        </button>
      </div>
    </form>
  );

  const renderForgotForm = () => (
    <form onSubmit={handleForgotSubmit} className="space-y-4" data-testid="forgot-form">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <KeyRound className="h-8 w-8 text-orange-600" />
        </div>
        <p className="text-gray-600 text-sm">Introdu email-ul asociat contului pentru a reseta parola.</p>
      </div>
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      <div className="space-y-2">
        <Label htmlFor="resetEmail">Adresa de Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input id="resetEmail" name="email" type="email" value={resetData.email} onChange={handleResetChange}
            className="pl-10" placeholder="exemplu@email.com" required data-testid="forgot-email" />
        </div>
      </div>
      <Button type="submit" className="w-full bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800"
        disabled={loading} data-testid="forgot-submit">
        {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Se verifica...</> : <><Mail className="mr-2 h-4 w-4" />Continua</>}
      </Button>
      <div className="text-center">
        <button type="button" onClick={() => setStep('credentials')} className="text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1 mx-auto">
          <ArrowLeft className="h-4 w-4" />Inapoi la autentificare
        </button>
      </div>
    </form>
  );

  const renderForgotChoose = () => (
    <div className="space-y-4" data-testid="forgot-choose">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <KeyRound className="h-8 w-8 text-orange-600" />
        </div>
        <p className="text-gray-600 text-sm">Alege metoda de verificare pentru resetarea parolei:</p>
      </div>
      <Button onClick={handleChooseTOTP} className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 h-14"
        data-testid="choose-totp">
        <Smartphone className="mr-3 h-5 w-5" />
        <div className="text-left">
          <div className="font-medium">Google Authenticator</div>
          <div className="text-xs opacity-80">Cod din aplicatie - instant</div>
        </div>
      </Button>
      <Button onClick={handleChooseEmail} variant="outline" className="w-full h-14 border-2" disabled={loading}
        data-testid="choose-email">
        {loading ? <Loader2 className="mr-3 h-5 w-5 animate-spin" /> : <Mail className="mr-3 h-5 w-5" />}
        <div className="text-left">
          <div className="font-medium">Cod pe Email</div>
          <div className="text-xs text-gray-500">Primesti cod pe {resetData.email}</div>
        </div>
      </Button>
      <div className="text-center">
        <button type="button" onClick={() => setStep('forgot')} className="text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1 mx-auto">
          <ArrowLeft className="h-4 w-4" />Inapoi
        </button>
      </div>
    </div>
  );

  const renderPasswordFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="newPassword">Parola noua</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input id="newPassword" name="newPassword" type={showNewPassword ? "text" : "password"} value={resetData.newPassword}
            onChange={handleResetChange} className="pl-10 pr-10" placeholder="Minim 8 caractere" required data-testid="new-password" />
          <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-3 text-gray-400 hover:text-gray-600">
            {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirma parola</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input id="confirmPassword" name="confirmPassword" type={showConfirmPassword ? "text" : "password"} value={resetData.confirmPassword}
            onChange={handleResetChange} className="pl-10 pr-10" placeholder="Repeta parola" required data-testid="confirm-password" />
          <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-3 text-gray-400 hover:text-gray-600">
            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </>
  );

  const renderResetForm = () => (
    <form onSubmit={handleResetSubmit} className="space-y-4" data-testid="reset-form">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="h-8 w-8 text-green-600" />
        </div>
        <p className="text-gray-600 text-sm">Introdu codul primit pe email si seteaza parola noua.</p>
      </div>
      {timeLeft > 0 ? (
        <div data-testid="reset-code-timer" className={`flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium ${
          timeLeft > 120 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            : timeLeft > 30 ? 'bg-amber-50 text-amber-700 border border-amber-200'
              : 'bg-red-50 text-red-700 border border-red-200 animate-pulse'}`}>
          <span className="flex items-center gap-2"><Clock className="h-4 w-4" />Cod valabil inca <strong>{formatTimeLeft()}</strong></span>
          <button type="button" data-testid="resend-code-btn" onClick={handleResendCode} disabled={resendCooldown > 0 || resending}
            className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-md transition-colors ${resendCooldown > 0 || resending ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-blue-600 hover:bg-blue-50 border border-blue-200'}`}>
            <RefreshCw className={`h-3 w-3 ${resending ? 'animate-spin' : ''}`} />
            {resending ? 'Se trimite...' : resendCooldown > 0 ? `Retrimite (${resendCooldown}s)` : 'Retrimite codul'}
          </button>
        </div>
      ) : codeExpiresAt ? (
        <div data-testid="reset-code-expired" className="flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium bg-red-50 text-red-700 border border-red-200">
          <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Codul a expirat.</span>
          <button type="button" onClick={handleResendCode} disabled={resending}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-white text-red-600 hover:bg-red-50 border border-red-200">
            <RefreshCw className={`h-3 w-3 ${resending ? 'animate-spin' : ''}`} />{resending ? 'Se trimite...' : 'Trimite cod nou'}
          </button>
        </div>
      ) : null}
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      <div className="space-y-2">
        <Label htmlFor="resetCode">Cod de resetare (6 cifre)</Label>
        <div className="relative">
          <KeyRound className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input id="resetCode" name="code" type="text" inputMode="numeric" value={resetData.code}
            onChange={(e) => setResetData({...resetData, code: e.target.value.replace(/\D/g, '').slice(0, 6)})}
            className="pl-10 text-center text-xl tracking-widest font-mono" placeholder="000000" maxLength={6} required data-testid="reset-code" />
        </div>
      </div>
      {renderPasswordFields()}
      <Button type="submit" className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800" disabled={loading} data-testid="reset-submit">
        {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Se salveaza...</> : <><CheckCircle className="mr-2 h-4 w-4" />Schimba parola</>}
      </Button>
      <div className="text-center">
        <button type="button" onClick={() => setStep('forgot')} className="text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1 mx-auto">
          <ArrowLeft className="h-4 w-4" />Inapoi
        </button>
      </div>
    </form>
  );

  const renderResetTOTPForm = () => (
    <form onSubmit={handleResetTOTPSubmit} className="space-y-4" data-testid="reset-totp-form">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Smartphone className="h-8 w-8 text-purple-600" />
        </div>
        <p className="text-gray-600 text-sm">Introdu codul din Google Authenticator si seteaza parola noua.</p>
      </div>
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      <div className="space-y-2">
        <Label htmlFor="totpCode">Cod Google Authenticator</Label>
        <div className="relative">
          <Smartphone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input id="totpCode" type="text" inputMode="numeric" value={resetData.code}
            onChange={(e) => setResetData({...resetData, code: e.target.value.replace(/\D/g, '').slice(0, 6)})}
            className="pl-10 text-center text-xl tracking-widest font-mono" placeholder="000000" maxLength={6} required data-testid="reset-totp-code" />
        </div>
        <p className="text-xs text-gray-500 text-center">Codul se schimba la fiecare 30 secunde</p>
      </div>
      {renderPasswordFields()}
      <Button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800" disabled={loading} data-testid="reset-totp-submit">
        {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Se salveaza...</> : <><CheckCircle className="mr-2 h-4 w-4" />Schimba parola</>}
      </Button>
      <div className="text-center">
        <button type="button" onClick={() => setStep('forgot-choose')} className="text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1 mx-auto">
          <ArrowLeft className="h-4 w-4" />Inapoi la alegere metoda
        </button>
      </div>
    </form>
  );

  const renderTOTPSetup = () => (
    <div className="space-y-4" data-testid="totp-setup">
      <div className="text-center mb-2">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Smartphone className="h-8 w-8 text-purple-600" />
        </div>
        <p className="text-gray-600 text-sm mb-4">
          Configureaza Google Authenticator pentru securitate suplimentara.
          Scanati codul QR cu aplicatia <strong>Google Authenticator</strong>.
        </p>
      </div>
      {totpSetup.qrCode && (
        <div className="flex justify-center p-4 bg-white rounded-lg border">
          <img src={`data:image/png;base64,${totpSetup.qrCode}`} alt="QR Code" className="w-48 h-48" data-testid="totp-qr-code" />
        </div>
      )}
      <div className="bg-gray-50 p-3 rounded-lg">
        <p className="text-xs text-gray-500 text-center mb-1">Sau introduceti manual cheia:</p>
        <p className="text-xs font-mono text-center select-all bg-white p-2 rounded border break-all" data-testid="totp-secret">{totpSetup.secret}</p>
      </div>
      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      <div className="space-y-2">
        <Label>Introdu codul din Authenticator pentru confirmare</Label>
        <div className="relative">
          <Smartphone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input type="text" inputMode="numeric" value={totpSetup.verifyCode}
            onChange={(e) => setTotpSetup(prev => ({...prev, verifyCode: e.target.value.replace(/\D/g, '').slice(0, 6)}))}
            className="pl-10 text-center text-2xl tracking-widest font-mono" placeholder="000000" maxLength={6} data-testid="totp-setup-code" />
        </div>
      </div>
      <Button onClick={handleTotpSetupConfirm} className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
        disabled={loading || totpSetup.verifyCode.length !== 6} data-testid="totp-setup-confirm">
        {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Se activeaza...</> : <><CheckCircle className="mr-2 h-4 w-4" />Activeaza Authenticator</>}
      </Button>
      <div className="text-center">
        <button type="button" onClick={finalizeLogin} className="text-sm text-gray-500 hover:text-gray-700" data-testid="totp-setup-skip">
          Sari peste (poti configura mai tarziu)
        </button>
      </div>
    </div>
  );

  const getTitle = () => {
    switch (step) {
      case '2fa': return 'Verificare in 2 pasi';
      case 'forgot': return 'Resetare parola';
      case 'forgot-choose': return 'Alege metoda';
      case 'reset': return 'Parola noua (Email)';
      case 'reset-totp': return 'Parola noua (Authenticator)';
      case 'totp-setup': return 'Configureaza Authenticator';
      default: return 'Autentificare';
    }
  };

  const getIcon = () => {
    switch (step) {
      case '2fa': return <Shield className="h-5 w-5" />;
      case 'forgot': case 'forgot-choose': return <KeyRound className="h-5 w-5" />;
      case 'reset': return <Lock className="h-5 w-5" />;
      case 'reset-totp': case 'totp-setup': return <Smartphone className="h-5 w-5" />;
      default: return <LogIn className="h-5 w-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="FINBRO Logo" className="w-16 h-16 rounded-xl mx-auto mb-4 object-cover" />
          <h1 className="text-2xl font-bold text-gray-900">FINBRO</h1>
          <p className="text-gray-600 mt-2">Platforma pentru intermediari de credit</p>
        </div>
        <Card className="shadow-lg border-0">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl text-center flex items-center justify-center gap-2">
              {getIcon()}{getTitle()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {step === 'credentials' && renderCredentialsForm()}
            {step === '2fa' && renderTwoFAForm()}
            {step === 'forgot' && renderForgotForm()}
            {step === 'forgot-choose' && renderForgotChoose()}
            {step === 'reset' && renderResetForm()}
            {step === 'reset-totp' && renderResetTOTPForm()}
            {step === 'totp-setup' && renderTOTPSetup()}
            {step === 'credentials' && (
              <>
                <div className="mt-6 pt-6 border-t">
                  <div className="text-center space-y-2">
                    <p className="text-sm text-gray-600">Nu aveti cont inca?</p>
                    <Link to="/register" className="text-blue-600 hover:text-blue-800 font-medium text-sm">Inregistrati-va ca intermediar</Link>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">Inapoi la pagina principala</Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
